/**
 * Settings' Close This Business (C-18, migration 0074; plan-documents/
 * PLAN_business_deletion_20261013.md §2.1). An admin's only: closing ends
 * everyone's access, so it is `manage_users`, which since 0070 is the admin
 * role's alone.
 *
 * WHAT IT SAYS BEFORE ANYTHING IS PRESSED, because closing cannot be undone
 * from inside the application:
 *
 *   - after closing nobody can sign in to this business, so its archive can no
 *     longer be downloaded: prepare and download it first, under Your Data
 *     (review BD-8 -- a precondition stated, not enforced);
 *   - its bank connections are disconnected and their credentials deleted;
 *   - its books are deleted within 90 days (the retention policy's §3), and
 *     only a written request to trey@technestack.com reopens it before then.
 *
 * Then the Matching screen's inline form: the business's name typed exactly,
 * and the six-digit code from the signed-in person's authenticator (the
 * operator's decision of 2026-10-13). The API answers a wrong code with a
 * sentence (422), never 401, since 401 here would mean "signed out".
 *
 * On success the session is already gone -- closing ended every one -- so the
 * application is loaded again from the start, which is the sign-in page.
 */

import { useState } from 'react';
import { ApiError, api } from '../api.js';
import { useAuth } from '../auth/AuthProvider.js';
import { reloadTo } from '../lib/reload.js';

export function CloseBusinessSection() {
  const { session, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!can('manage_users')) return null;
  const businessName = session?.businessName ?? 'this business';

  async function close() {
    setBusy(true);
    setError(null);
    try {
      await api.closeBusiness(name, code);
      reloadTo('/');
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.message !== caught.code ? caught.message : 'The business was not closed.',
      );
      setBusy(false);
    }
  }

  return (
    <section className="settings-section" aria-labelledby="settings-close">
      <h2 id="settings-close">Close This Business</h2>
      <p className="muted">
        Closing signs everyone out of {businessName} at once, and{' '}
        <strong>
          after closing nobody can sign in to this business, and its archive can no longer be downloaded
        </strong>
        : prepare and download it first, under Your Data. Its bank connections are disconnected and their
        credentials deleted.
      </p>
      <p className="muted">
        Its books, documents and archives are then deleted within 90 days. Until they are, a written request to{' '}
        <a href="mailto:trey@technestack.com">trey@technestack.com</a> reopens it.
      </p>

      {!open ? (
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setError(null);
          }}
        >
          Close This Business
        </button>
      ) : (
        <div className="new-account undo-form" role="group" aria-label="Close This Business">
          <label className="field">
            <span>Type The Business&apos;s Name</span>
            <input
              autoComplete="off"
              value={name}
              placeholder={businessName}
              onChange={(event) => {
                const next = event.target.value;
                setName(next);
              }}
            />
          </label>
          <label className="field">
            <span>Code From Your Authenticator</span>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => {
                const next = event.target.value;
                setCode(next);
              }}
            />
          </label>
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          <div className="row">
            <button type="button" disabled={busy} onClick={() => void close()}>
              {busy ? 'Closing…' : 'Close It'}
            </button>
            <button
              type="button"
              className="link"
              onClick={() => {
                setOpen(false);
                setName('');
                setCode('');
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
